/**
 * The Excel exports of the two appointment modules — `03-appointments.md` §A10 and §B11.
 *
 * **Everything in this file is pure, and that is what makes the tests real.** A builder takes
 * plain row data and returns a description of a workbook; nothing here touches `exceljs`, the
 * DOM, a clock or the network. `render.ts` turns a description into a real `.xlsx`, and a test
 * can therefore write one to a buffer, read it back and assert on the sheet a TD will open —
 * rather than assert that a mock was called.
 *
 * **The event's name comes from the tournament record, never from this file.** §A10 and §B11
 * spell their bases and titles as `AG2026-…` / `AG2026 Hockey — …`. Those literals predate the
 * B-1 fix that took fixed event names out of the chrome (see the module comment in
 * `app/Shell.tsx`): they were true of one installation and false of every other, the
 * demonstration database included. The base and the title are therefore derived from
 * `useTournament()`'s `shortDisplayName`, and where no tournament is known the workbook is
 * named after the application instead. A spreadsheet that went out of the room carrying the
 * wrong event's name would be worse than one carrying none.
 *
 * **Empty slots export blank** (never `—`). The dash is a table's way of saying "nothing here"
 * to a reader; in a spreadsheet it is a string in a column somebody will sort, filter or count,
 * and it would make an unfilled slot look filled.
 */

import { TOURNAMENT_TIMEZONE } from "../../app/tournamentTime.js";

/* ── What a sheet is ──────────────────────────────────────────────────────── */

/** A cell as the builders hand it over. `null` is the empty slot, and stays empty. */
export type CellValue = string | number | null;

/**
 * One sheet, fully described.
 *
 * The banner is four lines and not three: the title, the export stamp and the scope line are
 * the convention every sheet keeps, and the note is the line §B11 requires to travel WITH the
 * data — *"Rule violations are carried through so they cannot be hidden by exporting"* only
 * holds if the sentence saying so is at the top of the sheet rather than in a covering email.
 */
export interface SheetSpec {
  /** The tab name. Excel allows 31 characters and forbids `[]:*?/\`. */
  name: string;
  /** Row 1. */
  title: string;
  /** Row 3 — which filters the TD had applied when the button was pressed. */
  scope: string;
  /** Row 4, when there is one. */
  note: string | null;
  /** Orange banner and an advisory title, rather than the teal of a record. */
  advisory: boolean;
  headers: string[];
  rows: CellValue[][];
  /**
   * Column headers whose cells are tinted red when they carry text. Named by header rather
   * than by index so a column moving cannot silently move the tint onto its neighbour.
   */
  violationColumns: string[];
  /**
   * Column headers whose cells (header and data) are centre-aligned — the short factual
   * columns a reader scans down: dates, times, country codes, counts, status words. Named by
   * header for the same reason as {@link violationColumns}. Asked for by the Technical
   * Delegate on the first real workbooks (30 Aug 2026).
   */
  centredColumns: string[];
  /** Per-column widths, in Excel's character units. */
  widths: number[];
  /** Rendered bold — the matrix `Total` line. */
  boldRows: number[];
}

export interface WorkbookSpec {
  /** `Continental-Cup-2026-Fixture-Appointments` — the filename without stamp or extension. */
  base: string;
  /** Row 2 of every sheet: `Exported 30 Aug 2026 17:12 — TD Control Centre`. */
  exportedLine: string;
  sheets: SheetSpec[];
}

/* ── Naming ───────────────────────────────────────────────────────────────── */

/**
 * `Continental Cup 2026` → `Continental-Cup-2026`; nothing known → `TD-Control-Centre`.
 *
 * The fallback names the application rather than an event, which is the same answer the
 * masthead gives to the same question and for the same reason: `null` means either "the
 * session is still booting" or "this installation has no tournament", and neither permits an
 * event name to be printed.
 */
export function tournamentSlug(shortDisplayName: string | null): string {
  const slug = (shortDisplayName ?? "")
    .normalize("NFKD")
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "");
  return slug === "" ? "TD-Control-Centre" : slug;
}

/** The half of a sheet title that names the event, or the application when none is known. */
export function titlePrefix(shortDisplayName: string | null): string {
  const name = (shortDisplayName ?? "").trim();
  return name === "" ? "TD Control Centre" : name;
}

interface Stamp {
  /** `30 Aug 2026 17:12`. */
  human: string;
  /** `2026-08-30-1712`. */
  file: string;
}

/**
 * The one instant, written two ways, on the tournament's own clock where one is known.
 *
 * Both come from the same `Date` and the same zone on purpose — a filename stamped in the
 * device's zone beside a sheet stamped in the venue's is two different times presented as one,
 * and the whole point of the stamp is that the reader can tell which draft they are holding.
 */
export function exportStamp(at: Date, timezone: string | null): Stamp {
  // RUN 83 QA — a null tournament falls back to the TOURNAMENT's zone, never to `undefined`,
  // which is the DEVICE's. This is the same argument `matchDays.tournamentToday` carries: the
  // stamp is unlabelled, so a device answer is not a disclosed approximation, it is a wrong
  // time presented as the venue's. And `null` is the ordinary state, not the exotic one — the
  // tournament record is empty until its fetch lands, and can fail while the data fetch does
  // not. Every capture time in the All Evidence sheet comes through here.
  const zone = timezone ?? TOURNAMENT_TIMEZONE;
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hourCycle: "h23",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(at);
  const value = (type: string): string =>
    parts.find((part) => part.type === type)?.value ?? "";
  const month = new Intl.DateTimeFormat("en-GB", { timeZone: zone, month: "short" }).format(at);

  const day = value("day");
  const year = value("year");
  const hour = value("hour");
  const minute = value("minute");

  return {
    human: `${day} ${month} ${year} ${hour}:${minute}`,
    file: `${year}-${value("month")}-${day}-${hour}${minute}`,
  };
}

/** `{base}-YYYY-MM-DD-HHMM.xlsx`. */
export function exportFilename(base: string, at: Date, timezone: string | null): string {
  return `${base}-${exportStamp(at, timezone).file}.xlsx`;
}

/** The success line both pages print, verbatim in both. */
export function exportNotice(filename: string): string {
  return `Exported · ${filename} — timestamped in the filename and on the sheet.`;
}

function exportedLine(at: Date, timezone: string | null): string {
  return `Exported ${exportStamp(at, timezone).human} — TD Control Centre`;
}

/* ── The row data the containers hand over ────────────────────────────────── */

/** One appointment cell: who, and from where. Both blank where the slot is unfilled. */
export interface SlotCell {
  name: string | null;
  country: string | null;
}

/** Shared by all three fixture lists — the six columns that describe the match itself. */
export interface FixtureIdentity {
  /** `Men's` / `Women's`. */
  competition: string;
  /** `M01`, `W12` — `displayMatchNo`, never the per-competition `matchNo`. */
  match: string;
  /** `CHN v OMA`. */
  teams: string;
  date: string;
  time: string;
  pitch: string | null;
}

export interface TechnicalFixtureRow extends FixtureIdentity {
  to: SlotCell;
  sj: SlotCell;
  tj: SlotCell;
  /** The row's roll-up pill, as the Fixture List shows it. */
  status: string;
  /** `Country conflict on TO (overridden)`, or empty where the row is clean. */
  ruleCheck: string;
}

export interface TechnicalMatrixRow {
  official: string;
  country: string;
  role: string;
  to: number;
  sj: number;
  tj: number;
  roleTotal: number;
}

export interface TechnicalExportInput {
  /** `useTournament()?.shortDisplayName ?? null`. */
  tournament: string | null;
  /** The tournament's IANA zone, or null to stamp on the device's. */
  timezone: string | null;
  at: Date;
  /** What the Fixture List's own tabs were showing, e.g. `Men's · Pool matches`. */
  fixtureScope: string;
  /** What the Appointment Matrix's own tab was showing. */
  matrixScope: string;
  fixtures: TechnicalFixtureRow[];
  matrix: TechnicalMatrixRow[];
}

/* ── §A10 — the TO Appointments workbook ──────────────────────────────────── */

const TECHNICAL_FIXTURE_HEADERS = [
  "Competition",
  "Match",
  "Teams",
  "Date",
  "Time",
  "Pitch",
  "TO",
  "TO Country",
  "SJ",
  "SJ Country",
  "TJ",
  "TJ Country",
  "Status",
  "Rule Check",
];

const TECHNICAL_MATRIX_HEADERS = [
  "Official",
  "Country",
  "Role",
  "TO",
  "SJ",
  "TJ",
  "Role Total",
];

/** Blank rather than a dash, and blank rather than `0` — see the module note. */
function slotCells(cell: SlotCell): CellValue[] {
  return [cell.name ?? null, cell.country ?? null];
}

function blankIfEmpty(value: string): CellValue {
  return value === "" ? null : value;
}

export function buildTechnicalWorkbook(input: TechnicalExportInput): WorkbookSpec {
  const prefix = titlePrefix(input.tournament);

  const fixtures: SheetSpec = {
    name: "Fixture Appointments",
    title: `${prefix} — Fixture Appointments`,
    scope: input.fixtureScope,
    note: null,
    advisory: false,
    headers: TECHNICAL_FIXTURE_HEADERS,
    rows: input.fixtures.map((row) => [
      row.competition,
      row.match,
      row.teams,
      row.date,
      row.time,
      row.pitch ?? null,
      ...slotCells(row.to),
      ...slotCells(row.sj),
      ...slotCells(row.tj),
      row.status,
      blankIfEmpty(row.ruleCheck),
    ]),
    violationColumns: ["Rule Check"],
    centredColumns: [...CENTRED_IDENTITY, "TO Country", "SJ Country", "TJ Country", "Status"],
    widths: [12, 8, 14, 12, 8, 10, 22, 11, 22, 11, 22, 11, 14, 34],
    boldRows: [],
  };

  const totals = input.matrix.reduce(
    (running, row) => ({
      to: running.to + row.to,
      sj: running.sj + row.sj,
      tj: running.tj + row.tj,
      roleTotal: running.roleTotal + row.roleTotal,
    }),
    { to: 0, sj: 0, tj: 0, roleTotal: 0 },
  );

  const matrixRows: CellValue[][] = input.matrix.map((row) => [
    row.official,
    row.country,
    row.role,
    row.to,
    row.sj,
    row.tj,
    row.roleTotal,
  ]);
  matrixRows.push(["Total", null, null, totals.to, totals.sj, totals.tj, totals.roleTotal]);

  const matrix: SheetSpec = {
    name: "Appointment Matrix",
    title: `${prefix} — Appointment Matrix`,
    scope: input.matrixScope,
    note: "Counted live from the appointments on screen — the same arithmetic the page shows.",
    advisory: false,
    headers: TECHNICAL_MATRIX_HEADERS,
    rows: matrixRows,
    violationColumns: [],
    centredColumns: ["Country", "Role", "TO", "SJ", "TJ", "Role Total"],
    widths: [26, 10, 10, 8, 8, 8, 12],
    boldRows: [matrixRows.length - 1],
  };

  return {
    base: `${tournamentSlug(input.tournament)}-Fixture-Appointments`,
    exportedLine: exportedLine(input.at, input.timezone),
    sheets: [fixtures, matrix],
  };
}

/* ── §B11 — the two umpire workbooks ──────────────────────────────────────── */

export interface UmpireFixtureRow extends FixtureIdentity {
  u1: SlotCell;
  u2: SlotCell;
  ru: SlotCell;
  vu: SlotCell;
}

/** The TMS half carries the rules it failed; the advisory half carries a status instead. */
export interface UmpireTmsFixtureRow extends UmpireFixtureRow {
  /** `Passes`, or `2 Violations`. */
  ruleCheck: string;
  /** `U2: Two Roles In One Match · VU: Two Roles In One Match`, or empty. */
  violationDetail: string;
}

export interface UmpireAiFixtureRow extends UmpireFixtureRow {
  status: string;
}

export interface UmpireMatrixRow {
  umpire: string;
  country: string;
  onField: number;
  reserve: number;
  video: number;
}

export interface UmpireExportInput<Row> {
  tournament: string | null;
  timezone: string | null;
  at: Date;
  fixtureScope: string;
  matrixScope: string;
  fixtures: Row[];
  matrix: UmpireMatrixRow[];
}

const UMPIRE_IDENTITY_HEADERS = ["Competition", "Match", "Teams", "Date", "Time", "Pitch"];
/** The identity columns the TD asked to see centred, on every fixture sheet alike. */
const CENTRED_IDENTITY = ["Teams", "Date", "Time", "Pitch"];
const UMPIRE_SLOT_HEADERS = [
  "U1",
  "U1 Country",
  "U2",
  "U2 Country",
  "RU",
  "RU Country",
  "VU",
  "VU Country",
];

const UMPIRE_MATRIX_HEADERS = ["Umpire", "Country", "On-Field", "RU", "VU"];

/**
 * §B6's rule, stated on the sheet as well as on the screen. Three ledgers, never one figure —
 * a spreadsheet with a fourth column would have somebody adding them up by Thursday.
 */
const UMPIRE_MATRIX_NOTE =
  "On-field, reserve and video duties are reported separately and never summed.";

const TMS_NOTE =
  "Read-only record of the Umpires Managers' appointments as recorded in this system. Rule " +
  "violations are carried through so they cannot be hidden by exporting.";

const ADVISORY_NOTE =
  "Advisory — reference only. Umpire appointments are made by the Umpires Managers in TMS. " +
  "Nothing here is ever pushed.";

const IDENTITY_WIDTHS = [12, 8, 14, 12, 8, 10];
const SLOT_WIDTHS = [22, 11, 22, 11, 22, 11, 22, 11];

function identityCells(row: FixtureIdentity): CellValue[] {
  return [row.competition, row.match, row.teams, row.date, row.time, row.pitch ?? null];
}

function umpireSlotCells(row: UmpireFixtureRow): CellValue[] {
  return [...slotCells(row.u1), ...slotCells(row.u2), ...slotCells(row.ru), ...slotCells(row.vu)];
}

function umpireMatrixSheet(
  input: { matrix: UmpireMatrixRow[]; matrixScope: string },
  name: string,
  title: string,
  advisory: boolean,
): SheetSpec {
  return {
    name,
    title,
    scope: input.matrixScope,
    note: UMPIRE_MATRIX_NOTE,
    advisory,
    headers: UMPIRE_MATRIX_HEADERS,
    rows: input.matrix.map((row) => [
      row.umpire,
      row.country,
      row.onField,
      row.reserve,
      row.video,
    ]),
    violationColumns: [],
    centredColumns: ["Country", "On-Field", "RU", "VU"],
    widths: [26, 10, 12, 8, 8],
    boldRows: [],
  };
}

export function buildUmpireTmsWorkbook(
  input: UmpireExportInput<UmpireTmsFixtureRow>,
): WorkbookSpec {
  const prefix = titlePrefix(input.tournament);

  const fixtures: SheetSpec = {
    name: "Umpire Fixture List",
    title: `${prefix} — Umpire Fixture List (TMS Actual)`,
    scope: input.fixtureScope,
    note: TMS_NOTE,
    advisory: false,
    headers: [...UMPIRE_IDENTITY_HEADERS, ...UMPIRE_SLOT_HEADERS, "Rule Check", "Violation Detail"],
    rows: input.fixtures.map((row) => [
      ...identityCells(row),
      ...umpireSlotCells(row),
      row.ruleCheck,
      blankIfEmpty(row.violationDetail),
    ]),
    violationColumns: ["Violation Detail"],
    centredColumns: [
      ...CENTRED_IDENTITY,
      "U1 Country",
      "U2 Country",
      "RU Country",
      "VU Country",
    ],
    widths: [...IDENTITY_WIDTHS, ...SLOT_WIDTHS, 14, 44],
    boldRows: [],
  };

  return {
    base: `${tournamentSlug(input.tournament)}-Umpire-Fixture-List`,
    exportedLine: exportedLine(input.at, input.timezone),
    sheets: [
      fixtures,
      umpireMatrixSheet(
        input,
        "Umpire Matrix",
        `${prefix} — Umpire Appointment Matrix (TMS Actual)`,
        false,
      ),
    ],
  };
}

/**
 * The advisory workbook — the route by which the engine's draft reaches the Umpires Managers.
 *
 * §B11 is headed *"Export (TMS Actual Only)"*, and this deliberately supersedes it. The gap
 * that section itself records (**Gap O1**, `OPEN-QUESTIONS.md` Q5) is that there is no route
 * by which the advisory draft reaches the UMs at all; withholding the export does not make the
 * draft less advisory, it just means the TD retypes it. What keeps it honest is the labelling,
 * which is why the word `Advisory` is in the sheet title, in the tab name and on the note line
 * rather than only in the covering message somebody may not forward.
 */
export function buildUmpireAiWorkbook(input: UmpireExportInput<UmpireAiFixtureRow>): WorkbookSpec {
  const prefix = titlePrefix(input.tournament);

  const fixtures: SheetSpec = {
    name: "AI Reference (Advisory)",
    title: `${prefix} — Umpire Fixture List (AI Reference — Advisory Only)`,
    scope: input.fixtureScope,
    note: ADVISORY_NOTE,
    advisory: true,
    headers: [...UMPIRE_IDENTITY_HEADERS, ...UMPIRE_SLOT_HEADERS, "Status"],
    rows: input.fixtures.map((row) => [
      ...identityCells(row),
      ...umpireSlotCells(row),
      row.status,
    ]),
    violationColumns: [],
    centredColumns: [
      ...CENTRED_IDENTITY,
      "U1 Country",
      "U2 Country",
      "RU Country",
      "VU Country",
      "Status",
    ],
    widths: [...IDENTITY_WIDTHS, ...SLOT_WIDTHS, 16],
    boldRows: [],
  };

  return {
    base: `${tournamentSlug(input.tournament)}-Umpire-AI-Reference`,
    exportedLine: exportedLine(input.at, input.timezone),
    sheets: [
      fixtures,
      umpireMatrixSheet(
        input,
        "AI Matrix (Advisory)",
        `${prefix} — Umpire Appointment Matrix (AI Reference — Advisory Only)`,
        true,
      ),
    ],
  };
}

/* ── Spec 11 §5 — the consolidated umpire-feedback workbook ───────────────────
 *
 * THE FILE A UM WRITES HER END-OF-TOURNAMENT REPORTS FROM.
 *
 * Spec 11 §5 step 2 defines the evidence pool per umpire — *"all released notes from every UM
 * across the whole tournament, plus the owner's own locked notes"*, widened by ruling §I to
 * her own Drafts as well. Spec 06 §3 is the screen that shows it, one umpire at a time. This
 * is the same evidence, every umpire at once, in a form that works on an aeroplane: two
 * sheets, sortable, filterable, and printable by somebody who did not make it.
 *
 * **The privacy sentence travels ON THE SHEET.** The file carries the reader's own Draft and
 * Locked notes — notes the panel has never seen and, for a Locked one, never will. §B11's
 * lesson holds here for the opposite reason: a sentence that only exists in the covering
 * message is a sentence somebody will not forward. So the note line says whose file it is on
 * BOTH sheets, and every row carries its own `Draft` · `Locked` · `Released` label rather than
 * leaving the reader to infer it from the author column.
 *
 * **Teal and not orange.** The advisory band means *reference only, nothing here is a record*
 * (the AI half of §B11). This is a record — the panel's released notes, verbatim, plus the
 * reader's own — so it is dressed as one. The caution it carries is about who may READ the
 * file, not about whether its contents are true, and that caution is a sentence rather than a
 * colour.
 *
 * **No per-UM count anywhere** (spec 11 §6). The summary sheet counts, per umpire, the
 * released notes behind them, how many different managers contributed, and the reader's OWN
 * Drafts and Locked notes. Nothing is counted per author, and nothing of anybody else's
 * unreleased evidence is counted at all — it is not in the body this is built from.
 */

/**
 * `2026-09-18 14:32` — a captured instant, on the tournament's own clock where one is known.
 *
 * Sortable as text, unambiguous to read, and stamped in the SAME zone as the export line at
 * the top of the sheet, for the reason {@link exportStamp} gives: two clocks presented as one
 * is the defect, not the imprecision. RUN 83 QA: this comment used to say the scope line names
 * the clock. It does not — no scope string names a zone — so the stamp stands on being the
 * tournament's clock always, which is now what {@link exportStamp} guarantees.
 *
 * A value that is not a readable instant is passed through untouched rather than rendered as
 * `Invalid Date` — the wire said something, and the sheet reports what it said.
 */
export function capturedStamp(iso: string, timezone: string | null): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  const stamp = exportStamp(at, timezone);
  // `2026-09-18-1432` → `2026-09-18 14:32`. One formatter, one zone, two shapes of the same
  // instant — the file stamp's own parts, re-punctuated for a column a reader scans.
  const [year, month, day, time] = stamp.file.split("-");
  if (year === undefined || month === undefined || day === undefined || time === undefined) {
    return iso;
  }
  return `${year}-${month}-${day} ${time.slice(0, 2)}:${time.slice(2)}`;
}

/** One row of the `All Evidence` sheet — one note, about one umpire. */
export interface ConsolidatedEvidenceRow {
  umpire: string;
  country: string;
  /** `Draft` · `Locked` · `Released` — §H's vocabulary, verbatim, never a fourth word. */
  status: string;
  /** The author's short name, or the bare word `You` for the reader's own (spec 04 §2.4). */
  author: string;
  /** `M02`, or blank where the note was taken outside a match. */
  match: string | null;
  quarter: number | null;
  /** The match minute. Blank where none was recorded. */
  minute: number | null;
  /** The criterion's display name, or the pane's own name for the ungrouped bucket. */
  criterion: string;
  note: string;
  /** `2026-09-18 14:32` on the tournament's clock — see {@link capturedStamp}. */
  captured: string;
}

/** One row of the summary sheet — one umpire, and what stands behind their report. */
export interface ConsolidatedSummaryRow {
  umpire: string;
  country: string;
  /** Released notes about this umpire, from every manager. The shared, checkable evidence. */
  released: number;
  /** How many different managers contributed one. Never who, and never how many each. */
  authors: number;
  /** The reader's own, and only ever the reader's own. */
  yourDrafts: number;
  yourLocked: number;
}

export interface ConsolidatedEvidenceExportInput {
  /** `useTournament()?.shortDisplayName ?? null`. */
  tournament: string | null;
  timezone: string | null;
  at: Date;
  /** Row 3 of the evidence sheet — exactly what the sheet holds. */
  scope: string;
  /** Row 3 of the summary sheet. */
  summaryScope: string;
  rows: ConsolidatedEvidenceRow[];
  summary: ConsolidatedSummaryRow[];
}

const CONSOLIDATED_HEADERS = [
  "Umpire",
  "Ctry",
  "Status",
  "Author",
  "Match",
  "Q",
  "Min",
  "Criterion/Tag",
  "Note",
  "Captured",
];

const CONSOLIDATED_SUMMARY_HEADERS = [
  "Umpire",
  "Ctry",
  "Released Notes",
  "Contributing Authors",
  "Your Drafts",
  "Your Locked",
];

/**
 * The sentence that makes the file honest about itself, on every sheet of it.
 *
 * Verbatim, and in one place, so the two sheets cannot come to say different things about the
 * same download.
 */
const CONSOLIDATED_PRIVACY_NOTE =
  "Contains your own Draft and Locked notes — this file is yours alone; released notes are " +
  "the panel's shared record.";

/**
 * The honest empty sheet. A press that quietly did nothing would leave a manager wondering
 * whether the download failed or the tournament recorded nothing, and those are very
 * different things to be told.
 */
const NO_NOTES_ROW = "No Notes Recorded Yet";
const NO_UMPIRES_ROW = "No Umpires Assigned Yet";

function consolidatedRowCells(row: ConsolidatedEvidenceRow): CellValue[] {
  return [
    row.umpire,
    row.country,
    row.status,
    row.author,
    row.match,
    row.quarter,
    row.minute,
    row.criterion,
    row.note,
    row.captured,
  ];
}

/** One row of `width` cells, the first carrying `label` and the rest genuinely empty. */
function honestEmptyRow(label: string, width: number): CellValue[] {
  return [label, ...Array.from({ length: width - 1 }, (): CellValue => null)];
}

export function buildConsolidatedEvidenceWorkbook(
  input: ConsolidatedEvidenceExportInput,
): WorkbookSpec {
  const prefix = titlePrefix(input.tournament);

  const evidenceRows: CellValue[][] =
    input.rows.length === 0
      ? [honestEmptyRow(NO_NOTES_ROW, CONSOLIDATED_HEADERS.length)]
      : input.rows.map(consolidatedRowCells);

  const evidence: SheetSpec = {
    name: "All Evidence",
    title: `${prefix} — Umpire Feedback Evidence`,
    scope: input.scope,
    note: CONSOLIDATED_PRIVACY_NOTE,
    advisory: false,
    headers: CONSOLIDATED_HEADERS,
    rows: evidenceRows,
    violationColumns: [],
    centredColumns: ["Ctry", "Status", "Match", "Q", "Min", "Captured"],
    widths: [26, 8, 11, 14, 9, 5, 7, 26, 64, 18],
    boldRows: [],
  };

  const summaryRows: CellValue[][] =
    input.summary.length === 0
      ? [honestEmptyRow(NO_UMPIRES_ROW, CONSOLIDATED_SUMMARY_HEADERS.length)]
      : input.summary.map((row) => [
          row.umpire,
          row.country,
          row.released,
          row.authors,
          row.yourDrafts,
          row.yourLocked,
        ]);

  const summary: SheetSpec = {
    name: "Evidence Summary",
    title: `${prefix} — Umpire Feedback Summary`,
    scope: input.summaryScope,
    note: CONSOLIDATED_PRIVACY_NOTE,
    advisory: false,
    headers: CONSOLIDATED_SUMMARY_HEADERS,
    rows: summaryRows,
    violationColumns: [],
    centredColumns: [
      "Ctry",
      "Released Notes",
      "Contributing Authors",
      "Your Drafts",
      "Your Locked",
    ],
    widths: [26, 8, 15, 20, 12, 12],
    boldRows: [],
  };

  return {
    base: `${tournamentSlug(input.tournament)}-Umpire-Feedback-Evidence`,
    exportedLine: exportedLine(input.at, input.timezone),
    sheets: [evidence, summary],
  };
}
